import EventID from '@/model/EventClass/EventID'
import type { PublishPayType } from '@/publish/types'

const MAX_FLOWER_UNLOCK = 2000

interface FlowerEvent {
  Code?: number
  Parameter?: string[]
}

interface FlowerUnlockScanState {
  hasStoryLock: boolean
  flowerLockEvents: FlowerEvent[]
}

interface FlowerUnlockProject {
  storyTree: {
    Maps?: Array<{ List?: FlowerEvent[] }>
  }
  systemData: {
    customUI: {
      CUIList?: Array<{
        LoadEvent?: FlowerEvent[]
        AfterEvent?: FlowerEvent[]
        ControlList?: Array<{ EventList?: FlowerEvent[] }>
      }>
    }
    floatButton: {
      FBtnList?: Array<{ EventList?: FlowerEvent[] }>
    }
  }
}

export interface FlowerUnlockResult {
  payType: PublishPayType
  flowerUnlock: number
}

function parseInteger(value: unknown): number {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(parsed) ? parsed : 0
}

function scanEvents(events: FlowerEvent[] | undefined, state: FlowerUnlockScanState): void {
  for (const event of events ?? []) {
    if (event.Code === EventID.StoryLock) {
      state.hasStoryLock = true
      continue
    }
    if (event.Code === EventID.IF && event.Parameter?.[0]?.startsWith('FL')) {
      state.flowerLockEvents.push(event)
    }
  }
}

function scanProjectEvents(project: FlowerUnlockProject): FlowerUnlockScanState {
  const state: FlowerUnlockScanState = {
    hasStoryLock: false,
    flowerLockEvents: [],
  }

  for (const map of project.storyTree.Maps ?? []) {
    scanEvents(map.List, state)
  }
  for (const customUi of project.systemData.customUI.CUIList ?? []) {
    scanEvents(customUi.LoadEvent, state)
    scanEvents(customUi.AfterEvent, state)
    for (const control of customUi.ControlList ?? []) {
      scanEvents(control.EventList, state)
    }
  }
  for (const floatButton of project.systemData.floatButton.FBtnList ?? []) {
    scanEvents(floatButton.EventList, state)
  }

  return state
}

function getMaxFlowerLockValue(events: FlowerEvent[]): number {
  let maxValue = 0
  for (const event of events) {
    const isConstValue = event.Parameter?.[2] === '0'
    maxValue = Math.max(maxValue, isConstValue ? parseInteger(event.Parameter?.[3]) : 0)
  }
  return maxValue
}

/**
 * 按 3.0 发布逻辑自动计算收费方式下限和鲜花锁数量。
 * 商城入口暂隐藏，暂不扫描呼叫商城与 GameMallNew 商品价格。
 * @param project 当前唯一 MainProject
 * @param storedPayType ORG.pay 中记录的历史收费方式
 * @returns 发布接口使用的收费方式和 flower_unlock
 */
export function calculatePublishFlowerUnlock(project: FlowerUnlockProject, storedPayType: PublishPayType): FlowerUnlockResult {
  const state = scanProjectEvents(project)
  let payType: PublishPayType = 0
  let flowerUnlock = 0

  if (state.hasStoryLock) {
    payType = 3
  } else if (state.flowerLockEvents.length) {
    payType = 1
    flowerUnlock = getMaxFlowerLockValue(state.flowerLockEvents)
  }

  const finalPayType = Math.max(payType, storedPayType) as PublishPayType
  return {
    payType: finalPayType,
    flowerUnlock: finalPayType === 1
      ? Math.min(Math.max(0, Math.floor(flowerUnlock)), MAX_FLOWER_UNLOCK)
      : 0,
  }
}
